import { cn } from 'cn';
import { useState } from 'react';

import { Button, ParagraphWrapper } from '@vestigas/ui';

const DEFAULT_MAX_LINES = 3;

type CollapsibleCommentProps = {
  readonly comment: string;
  readonly maxLines?: number;
  readonly showLessLabel: string;
  readonly showMoreLabel: string;
} & ComponentStyling;

/**
 * Renders a comment with animated "show more" functionality for long text.
 * Always renders the first `maxLines` lines; remaining lines animate
 * open/closed using a CSS grid-rows transition (same technique as Reveal).
 * Accepts labels as props for i18n flexibility across modules.
 */
export function CollapsibleComment({
  className,
  comment,
  maxLines = DEFAULT_MAX_LINES,
  showLessLabel,
  showMoreLabel,
  style,
}: CollapsibleCommentProps) {
  const lines = comment.split(/\r?\n/);
  const isLong = lines.length > maxLines;
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleLines = lines.slice(0, maxLines);
  const overflowLines = lines.slice(maxLines);

  return (
    <div className={cn('text-sm', className)} style={style}>
      <ParagraphWrapper paragraphs={visibleLines} wrapIn="p" />
      {isLong ? (
        <>
          <div
            className={cn('grid', {
              'grid-rows-[0fr] duration-200 ease-in': !isExpanded,
              'grid-rows-[1fr] duration-300 ease-out': isExpanded,
            })}
            style={{ transitionProperty: 'grid-template-rows' }}
          >
            <div className="overflow-hidden">
              <ParagraphWrapper paragraphs={overflowLines} wrapIn="p" />
            </div>
          </div>
          <Button
            buttonStyle="link"
            className="-mt-2 -ml-1.5"
            size="small"
            text={isExpanded ? showLessLabel : showMoreLabel}
            onMouseDown={() => {
              setIsExpanded(!isExpanded);
            }}
          />
        </>
      ) : null}
    </div>
  );
}
